// The popover card. Renders each state of the flow and emits intent events the controller acts on.
// Events: start, cancel, send, discard, confirmDiscard, cancelDiscard, retry, close.
// All dynamic strings (errors, consent text) are set via textContent — never innerHTML.

import { mountWaveform } from './waveform.js';

export function createPanel(shadow, cfg) {
  const wrap = document.createElement('div');
  wrap.className = 'ff ff-hidden';
  // sits just above the launcher
  wrap.style.bottom = '76px';
  shadow.appendChild(wrap);

  const handlers = {};
  const on = (name, fn) => { (handlers[name] || (handlers[name] = [])).push(fn); };
  const emit = (name, payload) => (handlers[name] || []).forEach((fn) => fn(payload));

  let wave = null;
  const killWave = () => { if (wave) { wave.stop(); wave = null; } };

  function show() { wrap.classList.remove('ff-hidden'); }
  function hide() { killWave(); wrap.classList.add('ff-hidden'); wrap.innerHTML = ''; }

  // ---- Prepping ----
  // Deliberately NO waveform here: showing live mic input before Start made people think
  // recording had already begun. Nothing is visualized until they actually start.
  function showPrepping() {
    show();
    wrap.innerHTML = `
      <div class="ff-card">
        <button class="ff-x" data-act="cancel" type="button" aria-label="Close">&times;</button>
        <p class="ff-title">Record feedback</p>
        <p class="ff-sub">Talk us through what you'd change. Your screen activity on this page is captured too. Nothing records until you start.</p>
        <div class="ff-row" style="margin-top:2px;">
          <button class="ff-btn ff-btn-primary ff-btn-start" data-act="start" type="button"><span class="ff-start-dot"></span>Start recording</button>
        </div>
        <p class="ff-consent ff-hidden" data-el="consent"></p>
      </div>`;
    renderConsent();
    killWave();
    wrap_on('cancel', () => emit('cancel'));
    wrap_on('start', () => emit('start'));
  }

  function showPreppingDenied() {
    show();
    wrap.innerHTML = `
      <div class="ff-card">
        <button class="ff-x" data-act="cancel" type="button" aria-label="Close">&times;</button>
        <p class="ff-title">Microphone blocked</p>
        <p class="ff-sub">We need the mic for voice feedback. Allow it in your browser settings, or record without audio.</p>
        <div class="ff-row" style="margin-top:2px;">
          <button class="ff-btn ff-btn-primary ff-btn-start" data-act="start" type="button"><span class="ff-start-dot"></span>Record without audio</button>
        </div>
        <p class="ff-consent ff-hidden" data-el="consent"></p>
      </div>`;
    renderConsent();
    wrap_on('cancel', () => emit('cancel'));
    wrap_on('start', () => emit('start'));
  }

  function renderConsent() {
    if (!cfg.consentText) return;
    const el = wrap.querySelector('[data-el="consent"]');
    if (el) { el.textContent = cfg.consentText; el.classList.remove('ff-hidden'); }
  }

  // Disable interactive controls during async transitions (e.g. Start → capture running).
  function setBusy(busy) {
    wrap.querySelectorAll('.ff-btn').forEach((b) => { b.disabled = busy; });
  }

  // ---- Recording ----
  function showRecording(analyser) {
    show();
    wrap.innerHTML = `
      <div class="ff-card">
        <div class="ff-rec-head">
          <span class="ff-rec-dot"></span>
          <span class="ff-time"><span data-el="elapsed">0:00</span> <span class="ff-max" data-el="max">/ 20:00</span></span>
        </div>
        <canvas class="ff-wave" aria-hidden="true"></canvas>
        <div class="ff-progress" data-el="progress"><span data-el="bar"></span></div>
        <div class="ff-row" data-el="controls"></div>
      </div>`;
    killWave();
    wave = mountWaveform(wrap.querySelector('.ff-wave'), analyser, cfg.accent);
    renderControls();
  }

  function renderControls() {
    const row = wrap.querySelector('[data-el="controls"]');
    if (!row) return;
    row.innerHTML = `
      <button class="ff-btn ff-btn-danger" data-act="discard" type="button">Discard</button>
      <button class="ff-btn ff-btn-primary" data-act="send" type="button"><span class="ff-stop-sq"></span>Stop &amp; send</button>`;
    bind(row, 'discard', () => emit('discard'));
    bind(row, 'send', () => emit('send'));
  }

  function showDiscardConfirm() {
    const row = wrap.querySelector('[data-el="controls"]');
    if (!row) return;
    row.innerHTML = `
      <span class="ff-sub" style="flex:1;margin:0;align-self:center;">Discard recording?</span>
      <button class="ff-btn" data-act="no" type="button">No</button>
      <button class="ff-btn ff-btn-danger" data-act="yes" type="button">Yes, discard</button>`;
    bind(row, 'no', () => emit('cancelDiscard'));
    bind(row, 'yes', () => emit('confirmDiscard'));
  }

  function setTime(elapsedMs, maxMs) {
    const el = wrap.querySelector('[data-el="elapsed"]');
    const max = wrap.querySelector('[data-el="max"]');
    const bar = wrap.querySelector('[data-el="bar"]');
    const progress = wrap.querySelector('[data-el="progress"]');
    if (el) el.textContent = fmt(elapsedMs);
    if (max) max.textContent = '/ ' + fmt(maxMs);
    if (bar) bar.style.width = Math.min(100, (elapsedMs / maxMs) * 100) + '%';
    // Amber warning in the final stretch so the auto-send isn't a surprise.
    const warn = elapsedMs >= maxMs * 0.9;
    if (progress) progress.classList.toggle('ff-warn', warn);
    if (el) el.classList.toggle('ff-warn-text', warn);
  }

  // ---- Status states ----
  function showUploading() {
    killWave();
    show();
    wrap.innerHTML = `
      <div class="ff-card">
        <div class="ff-status"><span class="ff-spinner"></span><span data-el="msg">Sending feedback…</span></div>
      </div>`;
  }

  function setUploadAttempt(n, max) {
    const msg = wrap.querySelector('[data-el="msg"]');
    if (msg) msg.textContent = n > 1 ? `Retrying… (${n}/${max})` : 'Sending feedback…';
  }

  function showError(message, { retryable = true } = {}) {
    show();
    wrap.innerHTML = `
      <div class="ff-card">
        <p class="ff-title">Couldn't send</p>
        <p class="ff-err" data-el="err"></p>
        <div class="ff-row">
          <button class="ff-btn" data-act="close" type="button">Close</button>
          ${retryable ? '<button class="ff-btn ff-btn-primary" data-act="retry" type="button">Retry now</button>' : ''}
        </div>
      </div>`;
    wrap.querySelector('[data-el="err"]').textContent = message || 'Upload failed.';
    wrap_on('close', () => emit('close'));
    if (retryable) wrap_on('retry', () => emit('retry'));
  }

  function showDone({ dryRun = false } = {}) {
    killWave();
    show();
    // Only claim "sent" when an upload actually succeeded — in dry-run nothing left the browser.
    const msg = dryRun ? '✓ Recorded (test mode — no endpoint configured, nothing sent)' : '✓ Thanks — feedback sent.';
    wrap.innerHTML = `
      <div class="ff-card">
        <div class="ff-status"><span class="ff-ok" data-el="ok"></span></div>
      </div>`;
    wrap.querySelector('[data-el="ok"]').textContent = msg;
  }

  // helpers
  function wrap_on(act, fn) { bind(wrap, act, fn); }
  function bind(root, act, fn) {
    const el = root.querySelector(`[data-act="${act}"]`);
    if (el) el.addEventListener('click', fn);
  }

  return {
    on,
    show, hide, setBusy,
    showPrepping, showPreppingDenied,
    showRecording, renderControls, showDiscardConfirm, setTime,
    showUploading, setUploadAttempt, showError, showDone,
  };
}

function fmt(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  return Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0');
}
